import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Card,
  Col,
  DatePicker,
  Empty,
  PageHeader,
  Row,
  Spin,
  Tag,
  Tooltip,
  message,
  Switch,
  Divider,
} from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import ButtonItem from "../../../Components/ButtonItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import {
  construirHojaTabla,
  descargarLibro,
  limpiarNombreArchivo,
  texto,
  centro,
} from "../../../Components/ExcelTablaReporte";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
moment.locale("es");

const POLL_MS = 60000;

class EmpleadosEnSitio extends Component {
  state = {
    fecha: moment(),
    tiempoReal: true,
    departamentoId: null,
    departamentoNombre: "",
    empleadoId: null,
    empleadoNombre: "",

    loading: false,
    momento: null,
    total: 0,
    empleados: [],
  };

  pollTimer = null;

  componentDidMount() {
    this.consultar();
    this.startPolling();
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling = () => {
    this.stopPolling();
    if (!this.state.tiempoReal) return;
    this.pollTimer = setInterval(() => {
      if (this.state.tiempoReal) this.consultar({ silent: true });
    }, POLL_MS);
  };

  stopPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  };

  // =========================
  // API
  // =========================
  consultar = async ({ silent = false } = {}) => {
    if (!silent) this.setState({ loading: true });
    try {
      const body = {
        empresaId: this.props.empresaId,
      };
      if (!this.state.tiempoReal && this.state.fecha) {
        body.fecha = this.state.fecha.toISOString();
      }
      if (this.state.empleadoId) {
        body.empleadoId = this.state.empleadoId;
      } else if (this.state.departamentoId) {
        body.departamentoId = this.state.departamentoId;
      }

      const res = await axios.post(
        "reportesAsistencia/empleadosEnSitio",
        body,
        { headers: this.props.headersToken }
      );

      if (res.data?.success !== 2) {
        if (!silent) {
          message.error(res.data?.message || "No se pudo consultar empleados en sitio.");
        }
        this.setState({ momento: null, total: 0, empleados: [] });
      } else {
        const data = res.data.data || {};
        this.setState({
          momento: data.momento || null,
          total: data.total || 0,
          empleados: Array.isArray(data.empleados) ? data.empleados : [],
        });
      }
    } catch (err) {
      if (!silent) message.error("Error al consultar empleados en sitio.");
      this.setState({ momento: null, total: 0, empleados: [] });
    } finally {
      if (!silent) this.setState({ loading: false });
    }
  };

  // =========================
  // Events
  // =========================
  onChangeFecha = (value) => {
    this.setState({ fecha: value || moment() }, () => this.consultar());
  };

  onChangeTiempoReal = (checked) => {
    this.setState(
      { tiempoReal: checked, fecha: checked ? moment() : this.state.fecha },
      () => {
        if (checked) this.startPolling();
        else this.stopPolling();
        this.consultar();
      }
    );
  };

  onChangeDepartamento = (value, departamento) => {
    this.setState(
      {
        departamentoId: value || null,
        departamentoNombre: departamento?.stringBuscar || departamento?.nombre || "",
      },
      () => this.consultar()
    );
  };

  onChangeEmpleado = (value, option) => {
    const empleadoNombre =
      option?.label ||
      option?.children ||
      option?.nombre ||
      option?.title ||
      "";
    this.setState(
      { empleadoId: value || null, empleadoNombre },
      () => this.consultar()
    );
  };

  // =========================
  // Helpers de UI
  // =========================
  agruparPorDepartamento = (empleados) => {
    const grupos = new Map();
    empleados.forEach((emp) => {
      const key = emp.departamento || "—";
      if (!grupos.has(key)) grupos.set(key, []);
      grupos.get(key).push(emp);
    });
    return Array.from(grupos.entries());
  };

  formatTranscurrido = (min) => {
    if (min == null) return "—";
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h <= 0) return `${m} min`;
    return `${h}h ${m}m`;
  };

  // =========================
  // EXPORT: Excel
  // =========================
  exportExcel = () => {
    const { empleados, momento, departamentoNombre, empleadoNombre } = this.state;
    if (!empleados?.length) {
      message.warning("No hay datos para exportar.");
      return;
    }

    const columnas = [
      { titulo: "Departamento", ancho: 20 },
      { titulo: "Empleado", ancho: 30 },
      { titulo: "Horario", ancho: 24 },
      { titulo: "Entrada Prog.", ancho: 13 },
      { titulo: "Salida Prog.", ancho: 13 },
      { titulo: "Entrada Reg.", ancho: 13 },
      { titulo: "Fecha Entrada", ancho: 14 },
      { titulo: "Tiempo en sitio", ancho: 15 },
    ];

    const filas = empleados.map((e) => [
      texto(e.departamento || "—"),
      texto(e.nombre || "—"),
      texto((e.horario || "—").toString().replace(/\r?\n/g, " ")),
      centro(e.horaEntradaProgramada || "—"),
      centro(`${e.horaSalidaProgramada || "—"}${e.salidaDiaSiguiente ? " (+1)" : ""}`),
      centro(e.entradaRegistrada || "—"),
      centro(e.entradaRegistradaFecha || "—"),
      centro(this.formatTranscurrido(e.minutosTranscurridos)),
    ]);

    const descripcionFiltros = [
      `Momento: ${momento || "—"}`,
      `Departamento: ${departamentoNombre || "Todos"}`,
      `Empleado: ${empleadoNombre || "Todos"}`,
    ].join(" · ");

    descargarLibro(
      [
        {
          nombre: "Empleados en Sitio",
          hoja: construirHojaTabla({ columnas, filas, totales: [] }, {
            titulo: "Empleados en Sitio",
            empresaNombre: this.props.empresa,
            descripcionFiltros,
          }),
        },
      ],
      limpiarNombreArchivo(`empleados_en_sitio_${moment().format("YYYYMMDD_HHmm")}`)
    );
  };

  // =========================
  // EXPORT: PDF
  // =========================
  exportPDF = () => {
    const { empleados, momento } = this.state;
    if (!empleados?.length) {
      message.warning("No hay datos para exportar.");
      return;
    }

    const body = [
      [
        { text: "Departamento", bold: true },
        { text: "Empleado", bold: true },
        { text: "Horario", bold: true },
        { text: "Entrada Prog.", bold: true },
        { text: "Salida Prog.", bold: true },
        { text: "Entrada Reg.", bold: true },
        { text: "Tiempo en sitio", bold: true },
      ],
    ];

    empleados.forEach((e) => {
      body.push([
        e.departamento || "—",
        e.nombre || "—",
        e.horario || "—",
        e.horaEntradaProgramada || "—",
        `${e.horaSalidaProgramada || "—"}${e.salidaDiaSiguiente ? " (+1)" : ""}`,
        e.entradaRegistrada || "—",
        this.formatTranscurrido(e.minutosTranscurridos),
      ]);
    });

    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [30, 50, 30, 40],
      content: [
        { text: "Empleados en Sitio", style: "title" },
        {
          columns: [
            {
              width: "*",
              text: [
                { text: "Momento: ", bold: true },
                momento || "—",
              ],
            },
            {
              width: "auto",
              text: [
                { text: "Total: ", bold: true },
                `${empleados.length}`,
              ],
            },
          ],
          margin: [0, 8, 0, 12],
        },
        {
          table: {
            headerRows: 1,
            widths: [90, "*", 80, 60, 70, 60, 70],
            body,
          },
          layout: "lightHorizontalLines",
          fontSize: 9,
        },
        {
          text: `Generado: ${moment().format("DD/MM/YYYY HH:mm")}`,
          style: "footer",
          margin: [0, 10, 0, 0],
        },
      ],
      styles: {
        title: { fontSize: 14, bold: true },
        footer: { fontSize: 9, color: "#666666" },
      },
      defaultStyle: { fontSize: 10 },
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`empleados_en_sitio_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  // =========================
  // UI
  // =========================
  renderListado() {
    const { empleados } = this.state;

    if (!empleados?.length) {
      return (
        <div style={{ padding: 20 }}>
          <Empty description="Ningún empleado en sitio en este momento" />
        </div>
      );
    }

    const grupos = this.agruparPorDepartamento(empleados);

    return (
      <div style={{ maxHeight: "calc(100vh - 250px)", overflow: "auto", padding: 12 }}>
        {grupos.map(([departamento, lista]) => (
          <div key={departamento} style={{ marginBottom: 16 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#00BFD6",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <TeamOutlined />
              {departamento}
              <Tag color="cyan" style={{ marginLeft: 6 }}>
                {lista.length}
              </Tag>
            </div>

            {lista.map((e) => (
              <Card
                key={e.empleadoId}
                size="small"
                style={{ marginBottom: 8 }}
                bodyStyle={{ padding: 12 }}
              >
                <Row gutter={[10, 6]} align="top">
                  <Col flex="240px">
                    <div style={{ fontWeight: 600 }}>{e.nombre}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>
                      {e.horario || "Sin horario"}
                    </div>
                  </Col>

                  <Col flex="auto">
                    <div style={{ fontSize: 12, color: "#666" }}>
                      <span style={{ fontWeight: 600 }}>Programado:</span>{" "}
                      {e.horaEntradaProgramada || "—"} —{" "}
                      {e.horaSalidaProgramada || "—"}
                      {e.salidaDiaSiguiente && (
                        <Tag color="orange" style={{ marginLeft: 6 }}>
                          +1 día
                        </Tag>
                      )}
                    </div>

                    <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                      <span style={{ fontWeight: 600 }}>Entrada registrada:</span>{" "}
                      {e.entradaRegistrada || "—"}
                      {e.entradaRegistradaFecha && (
                        <span style={{ color: "#999", marginLeft: 6 }}>
                          ({e.entradaRegistradaFecha})
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col>
                    <Tag color="green" icon={<ClockCircleOutlined />}>
                      {this.formatTranscurrido(e.minutosTranscurridos)}
                    </Tag>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        ))}
      </div>
    );
  }

  render() {
    const {
      loading,
      fecha,
      tiempoReal,
      departamentoId,
      empleadoId,
      empleadoNombre,
      momento,
      total,
      empleados,
    } = this.state;

    return (
      <>
        <PageHeader
          title="Empleados en Sitio"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <>
              <Tooltip title="Actualizar">
                <ButtonItem
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={() => this.consultar()}
                >
                  Actualizar
                </ButtonItem>
              </Tooltip>

              <Tooltip title="Exportar a Excel">
                <ButtonItem
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={this.exportExcel}
                  disabled={!empleados?.length}
                >
                  Excel
                </ButtonItem>
              </Tooltip>

              <Tooltip title="Exportar a PDF">
                <ButtonItem
                  type="default"
                  icon={<FilePdfOutlined />}
                  onClick={this.exportPDF}
                  disabled={!empleados?.length}
                >
                  PDF
                </ButtonItem>
              </Tooltip>
            </>
          }
        />

        <Row gutter={10}>
          {/* Panel filtros */}
          <Col xs={24} md={8}>
            <Card
              title="Filtros"
              size="small"
              style={{
                minHeight: "80vh",
                borderRadius: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 12, color: "#666" }}>Tiempo real</span>
                  <Switch
                    checked={tiempoReal}
                    onChange={this.onChangeTiempoReal}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                  />
                </div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                  Refresca automáticamente cada {POLL_MS / 1000}s.
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                  Fecha y hora
                </div>
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="DD/MM/YYYY HH:mm"
                  value={fecha}
                  onChange={this.onChangeFecha}
                  style={{ width: "100%" }}
                  allowClear={false}
                  disabled={tiempoReal}
                />
                {tiempoReal && (
                  <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                    Activa "Tiempo real = Off" para consultar un momento histórico.
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                  Departamento
                </div>
                <SelectDepartamentosItem
                  empresaId={this.props.empresaId}
                  headersToken={this.props.headersToken}
                  value={departamentoId}
                  onChange={this.onChangeDepartamento}
                  allowClear
                  width="100%"
                  placeholder="Todos los departamentos"
                  sinBorde
                  disabled={!!empleadoId}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                  Empleado
                </div>
                <SelectEmpleadosItem
                  empresaId={this.props.empresaId}
                  headersToken={this.props.headersToken}
                  sinBorde
                  dropdownMatchSelectWidth={600}
                  value={empleadoId}
                  onChange={this.onChangeEmpleado}
                  allowClear
                  width="100%"
                  placeholder="Todos los empleados"
                />
                {!!empleadoNombre && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#999" }}>
                    Seleccionado: {empleadoNombre}
                  </div>
                )}
              </div>

              <Divider style={{ margin: "16px 0" }} />

              <div style={{ fontSize: 12, color: "#666" }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Momento:</span>{" "}
                  {momento || "—"}
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Total en sitio:</span>{" "}
                  <Tag color="cyan">{total}</Tag>
                </div>
              </div>
            </Card>
          </Col>

          {/* Panel listado */}
          <Col xs={24} md={16}>
            <Card
              title="En sitio"
              size="small"
              extra={loading ? <Tag color="processing">Cargando...</Tag> : null}
              bodyStyle={{ padding: 0 }}
              style={{
                minHeight: "80vh",
                borderRadius: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <Spin spinning={loading}>{this.renderListado()}</Spin>
            </Card>
          </Col>
        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(EmpleadosEnSitio);
